import {
  Box,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  HStack,
  Text,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import { getCountryDisplayName } from "../helpers/country-display";
import { countries as countriesData } from "../helpers/countries";

type CountryOption = {
  code: string;
  label: string;
};

type Props = {
  id: string;
  value?: string;
  onChange: (value: string) => void;
};

const normalizeSearchValue = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const CountrySelect = ({ id, value, onChange }: Props) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const options = useMemo<CountryOption[]>(() => {
    const language = i18n.language.startsWith("en") ? "en" : "es";
    return countriesData
      .map((country) => ({
        code: country.code,
        label: getCountryDisplayName(country, language),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language]);

  const selectedOption = useMemo(
    () => options.find((country) => country.code === value),
    [options, value]
  );

  useEffect(() => {
    if (isOpen) return;
    setQuery(selectedOption?.label ?? "");
  }, [isOpen, selectedOption]);

  const filteredOptions = useMemo(() => {
    const needle = normalizeSearchValue(query).trim();
    if (!needle) return options;
    return options.filter((country) => {
      const label = normalizeSearchValue(country.label);
      const code = country.code.toLowerCase();
      return label.includes(needle) || code.includes(needle);
    });
  }, [options, query]);

  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsOpen(false),
  });

  const hasValue = query.trim().length > 0 || !!value;

  return (
    <Box position="relative" ref={dropdownRef}>
      <InputGroup>
        {hasValue && (
          <InputRightElement h="full" display="flex" alignItems="center" pr={1}>
            <IconButton
              aria-label={t("personalDetails.form.countryClear")}
              icon={
                <Icon boxSize={4}>
                  <XMarkIcon />
                </Icon>
              }
              size="sm"
              variant="ghost"
              borderRadius="full"
              minW="36px"
              h="36px"
              onClick={() => {
                setQuery("");
                onChange("");
                setIsOpen(true);
                inputRef.current?.focus();
              }}
            />
          </InputRightElement>
        )}
        <Input
          id={id}
          ref={inputRef}
          type="text"
          pr={10}
          placeholder={t("personalDetails.form.countryPlaceholder")}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${id}-options`}
          aria-autocomplete="list"
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
        />
      </InputGroup>
      {isOpen && (
        <Box
          id={`${id}-options`}
          role="listbox"
          position="absolute"
          zIndex={10}
          mt={2}
          w="full"
          maxH="220px"
          overflowY="auto"
          borderWidth={1}
          borderColor="slate.200"
          bg="white"
          borderRadius="12px"
          boxShadow="lg"
        >
          {filteredOptions.map((country) => (
            <Box
              key={country.code}
              px={4}
              py={3}
              cursor="pointer"
              _hover={{ bg: "slate.50" }}
              onClick={() => {
                setQuery(country.label);
                onChange(country.code);
                setIsOpen(false);
              }}
            >
              <HStack spacing={3}>
                <ReactCountryFlag
                  countryCode={country.code}
                  svg
                  style={{ width: "1.2em", height: "1.2em" }}
                  aria-label={country.label}
                />
                <VStack align="start" spacing={0} minW={0}>
                  <Text fontSize="16px" fontWeight={500}>{country.label}</Text>
                  <Text fontSize="14px" color="slate.500">
                    {country.code}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          ))}
          {filteredOptions.length === 0 && (
            <Text px={4} py={3} color="slate.500" fontSize="14px">
              {t("personalDetails.form.countryNoResults")}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
